import React from 'react';
import { useParams } from 'react-router-dom';
import { DxContainer, DxCard, DxList, DxSpacer } from '@dvag/design-system-react';

import { useGetHouseholdDashboard } from 'hooks/useGetHouseholdDashboard';
import { useServiceAnalyseEnabled } from 'hooks/useServiceAnalyseEnabled';
import { useServiceAnalyseConsentState } from 'hooks/useServiceAnalyseConsentState';
import { hasApprovedConsent } from 'utils/util';
import { showMessage } from '@dvag/dfs-orchestrator-client/messengers';

import { NotificationErrorData } from 'utils/notification/types';
import { useGetFavoritesWithAction } from 'hooks/useGetFavoritesWithAction';
import { getUpperList } from './getUpperList';
import { Item } from './Item';

import './style.css';

export const UpperList = ({ personId }: { personId: string | undefined }) => {
  const { householdId, selectedPersonId } = useParams();
  const householdDashboard = useGetHouseholdDashboard();
  const hasHousehold = !!householdId;
  const disabledClass = !hasHousehold ? 'disabled-container' : '';

  const hasAtLeastOneConsent = !!householdDashboard.data?.consentList?.find((consent) =>
    hasApprovedConsent(consent.status),
  );

  const { pageAction } = useGetFavoritesWithAction(householdId);
  const showServiceAnalyse = useServiceAnalyseEnabled();
  const serviceAnalyseConsentState = useServiceAnalyseConsentState();
  const hasServiceAnalyseConsent =
    serviceAnalyseConsentState === 'partial' || serviceAnalyseConsentState === 'full';

  const upperList = getUpperList({
    householdId,
    personId,
    hasAtLeastOneConsent,
    hasServiceAnalyseConsent,
    pageAction,
    showServiceAnalyse,
    showPlanning: true,
    showShoppingCart: true,
    showBankConnection: false,
    showAnalysisEvaluation: true,
    showCoachReport: true,
    showAlert: (data?: NotificationErrorData) =>
      showMessage(data?.title ?? '', data?.description ?? '', 'error', 'modal'),
    isHouseholdDataFetched: householdDashboard.isFetched,
  });

  const renderItem = ({
    title,
    subtitle,
    image,
    chevronIcon,
    onClick,
    id,
    display,
    linkList,
  }: (typeof upperList)[number]) => (
    <Item
      disabledClassName={disabledClass}
      key={id}
      id={id}
      title={title}
      subtitle={subtitle}
      image={image}
      chevronIcon={chevronIcon}
      onClick={onClick}
      display={display}
      linkList={linkList}
      hasHousehold={hasHousehold}
    />
  );

  const mainItems = upperList.filter((item) => !item.detached);
  const detachedItems = upperList.filter((item) => item.detached && item.display);

  return (
    <DxContainer
      color="transparent"
      className={`${disabledClass && !selectedPersonId ? 'dashboard-item-container-wr' : ''}`}
      data-testid="upper-list-container"
    >
      <DxCard color="transparent">
        <DxList>{mainItems.map(renderItem)}</DxList>
      </DxCard>
      {detachedItems.length > 0 && (
        <>
          <DxSpacer size="24v" />
          <DxCard color="transparent">
            <DxList>{detachedItems.map(renderItem)}</DxList>
          </DxCard>
        </>
      )}
    </DxContainer>
  );
};
